import { useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format, parseISO } from 'date-fns';
import { FileText } from 'lucide-react';
import { useDocsStore } from '../../stores/docsStore';
import { createMarkdownComponents } from '../../utils/markdownConfig';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function LogContent() {
  const { legacyContent, contentLoading, selectedDate } = useDocsStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  const markdownComponents = useMemo(() => createMarkdownComponents(), []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedDate]);

  if (!selectedDate) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={<FileText />}
          title="Select a date to view logs"
          description="Choose a date from the sidebar to browse SecondBrain's daily memory logs."
        />
      </div>
    );
  }

  if (contentLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!legacyContent) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState
          icon={<FileText />}
          title="No log for this date"
          description={`No memory log was recorded for ${selectedDate}.`}
        />
      </div>
    );
  }

  const dateFormatted = format(parseISO(legacyContent.date), 'MMMM d, yyyy');

  return (
    <div ref={scrollRef} className="overflow-y-auto h-full p-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-text-primary">{dateFormatted}</h1>
        <p className="text-xs text-text-muted mt-1">{formatFileSize(legacyContent.size)}</p>
      </div>
      <div className="max-w-3xl">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {legacyContent.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default LogContent;
